import { useRef, useMemo } from 'react';
import isEqual from 'lodash/isEqual';

/**
 * useMemo with deep equality comparison
 * Only updates when the value has actually changed (deep comparison)
 * Prevents unnecessary re-renders from new object references with identical content
 *
 * Note: This is an advanced hook that intentionally uses dynamic deps array.
 * The deps array is passed through from the caller, which is the intended behavior.
 */
export function useDeepMemo(factory, deps) {
  const valueRef = useRef();
  const depsRef = useRef();

  // Dynamic deps array is intentional - we're creating a wrapper around useMemo

  return useMemo(() => {
    const newValue = factory();

    // First render or deps changed
    if (!depsRef.current || !isEqual(depsRef.current, deps)) {
      depsRef.current = deps;

      // Only update if value actually changed (deep comparison)
      if (!isEqual(valueRef.current, newValue)) {
        valueRef.current = newValue;
      }
    }

    return valueRef.current;
  }, deps);
}

/**
 * Specialized version for subscription data
 * Handles the common case of subscription.data?.field || defaultValue
 */
export function useStableSubscriptionData(data, defaultValue = []) {
  const dataRef = useRef(defaultValue);

  return useMemo(() => {
    const newData = data || defaultValue;

    // Only update reference if content actually changed
    if (!isEqual(dataRef.current, newData)) {
      dataRef.current = newData;
    }

    return dataRef.current;
  }, [data, defaultValue]);
}
